/**
 * Reglas de pantalla de la invitación del perito (tanda 5, 26-09-2026), SIN
 * dependencias: las importan los tests tal cual. Las llamadas al servidor viven
 * en `peritoInvitaciones.ts`, que reexporta todo esto.
 */

/** Largo máximo de un código: los nuevos son de 10, los antiguos de 8. */
export const LARGO_CODIGO = 10;

/**
 * Lo que se teclea o se pega en el campo del código. Primero se quita todo lo que
 * no sea letra o número y DESPUÉS se corta: al revés, un espacio delante (como
 * llega por WhatsApp) se comía el último carácter (T01-11).
 */
export function normalizarCodigo(texto: string): string {
  return texto.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, LARGO_CODIGO);
}

/** Errores del canje que devuelve el servidor (un texto por caso, T01-12). */
export type ErrorDeCanje =
  | "INVALID_CODE"
  | "EXPIRED"
  | "REVOKED"
  | "ALREADY_USED"
  | "ALREADY_LINKED"
  | "ALREADY_MEMBER"
  | "NOT_A_PERITO"
  | "RATE_LIMITED"
  | "NOT_AUTHENTICATED"
  | "EMAIL_EXISTS"
  | "WEAK_PASSWORD"
  | "INVALID_EMAIL";

const CLAVE_DE_ERROR: Record<ErrorDeCanje, string> = {
  INVALID_CODE: "perito.invitacion.errores.noExiste",
  EXPIRED: "perito.invitacion.errores.caducado",
  REVOKED: "perito.invitacion.errores.anulado",
  ALREADY_USED: "perito.invitacion.errores.yaUsado",
  ALREADY_LINKED: "perito.invitacion.errores.yaVinculado",
  ALREADY_MEMBER: "perito.invitacion.errores.yaMiembro",
  NOT_A_PERITO: "perito.invitacion.errores.noEsPerito",
  RATE_LIMITED: "perito.invitacion.errores.demasiadosIntentos",
  NOT_AUTHENTICATED: "perito.invitacion.errores.sinSesion",
  EMAIL_EXISTS: "perito.invitacion.errores.correoYaExiste",
  WEAK_PASSWORD: "perito.invitacion.errores.contrasenaDebil",
  INVALID_EMAIL: "perito.invitacion.errores.correoInvalido",
};

/** Clave de i18n para un error del canje; lo desconocido cae en el genérico. */
export function claveDeErrorDeCanje(codigo: string | null | undefined): string {
  return (codigo && CLAVE_DE_ERROR[codigo as ErrorDeCanje]) || "perito.invitacion.errores.generico";
}

/** Ruta pública del enlace de invitación (fuera de RoleBasedRouter, en App.tsx). */
export const RUTA_UNIRSE = "/unirse/perito";

/** El enlace que se comparte. `origen` = window.location.origin (se pasa para poder probarlo). */
export function enlaceDeInvitacion(token: string, origen: string): string {
  return `${origen.replace(/\/$/, "")}${RUTA_UNIRSE}?t=${encodeURIComponent(token)}`;
}

/** Enlace de WhatsApp con el texto ya escrito. */
export function enlaceWhatsApp(texto: string): string {
  return `https://wa.me/?text=${encodeURIComponent(texto)}`;
}

/** Enlace de correo con asunto y cuerpo. */
export function enlaceCorreo(asunto: string, cuerpo: string): string {
  return `mailto:?subject=${encodeURIComponent(asunto)}&body=${encodeURIComponent(cuerpo)}`;
}

/**
 * Lo que pega el perito sin empresa (P3, 26-09-2026): el ENLACE entero que le
 * mandaron por WhatsApp o correo (`…/unirse/perito?t=<token>`) o el CÓDIGO corto.
 * Con enlace se canjea el token; si no, se limpia como un código.
 */
export function leerCodigoOEnlace(texto: string): { tipo: "enlace"; token: string } | { tipo: "codigo"; codigo: string } | null {
  const limpio = (texto ?? "").trim();
  if (!limpio) return null;
  const token = /[?&]t=([A-Za-z0-9]+)/.exec(limpio)?.[1];
  if (token) return { tipo: "enlace", token: token.toLowerCase() };
  const codigo = normalizarCodigo(limpio);
  return codigo ? { tipo: "codigo", codigo } : null;
}
